import { logError, logWarn, logOperationalEvent } from 'saddlebag-logger';

import type { Logging } from '@shared/types-frontend/src/client-side-params';
import type { Logger as LoggerType } from '@shared/types-frontend/src/logger';
import type { NewRelicFields } from '@shared/types-universal/src/utils';

const namespace = 'hotels-website';
// The `logging` object is initialized as an empty object and populated via `initLogging` when the application starts
const logging: Partial<Logging> = {};

export const initLogging = (props: Logging) => {
  if (Object.keys(logging).length === 0) {
    Object.assign(logging, props);
  }
};

const wrapError = (msg: string, error?: Error | unknown) => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(msg);
};

// The type definitions in saddlebag-logger are not allowed to have any fields that are undefined or null
// so add this function to filter out any invalid fields to avoid type check errors
const removeInvalidFields = (fields: NewRelicFields) => {
  const validFields: NewRelicFields = {};
  Object.keys(fields).forEach((key) => {
    const value = fields[key];
    if (typeof value === 'string' || typeof value === 'number') {
      validFields[key] = value;
    }
  });
  return validFields;
};

const Logger: LoggerType = {
  // 1. For events to view in dashboards or alert
  // 2. For non-warning or non-error logging
  //
  // `component` defaults to the app-wide namespace, but callers can override it by including a
  // `component` field in `data` — useful for operational events that need to be grouped by
  // feature (e.g. 'trip_builder_context_menu') rather than by app, to stay comparable with the
  // same event emitted from other apps/verticals.
  event: (eventName, data) => {
    const validFields = removeInvalidFields({ ...data, ...logging });
    logOperationalEvent({
      ...validFields,
      component:
        typeof data?.component === 'string' ? data.component : namespace,
      eventName,
    });
  },

  warn: (msg, data) => {
    logWarn({
      ...removeInvalidFields({ ...data, ...logging }),
      component: namespace,
      message: msg,
    });
  },

  error: (msg, data, error) => {
    const errorObject = wrapError(msg, error);

    logError(errorObject, {
      ...data,
      ...logging,
      component: namespace,
      message: msg,
    });
  },
};

export default Logger;
